import {
  AddIcon,
  Button,
  Card,
  CardHeader,
  Divider,
  EmptyState,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Tag,
  Typography,
} from '@all-manager/design-system-v2';
import ConfirmDeleteDialog from '../../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import CreateEditDocumentTypeModal from '../CreateEditDocumentTypeModal/CreateEditDocumentTypeModal';
import DocumentTypeMenuAction from '../DocumentTypeMenuAction/DocumentTypeMenuAction';
import useDocumentTypesSettingsCard, { Props } from './useDocumentTypesSettingsCard';

// Keeps the French quotes and question mark on the type name's line.
const NBSP = ' ';
const SKELETON_ROWS = [0, 1, 2, 3];

export default function DocumentTypesSettingsCard(props: Props) {
  const hook = useDocumentTypesSettingsCard(props);

  return (
    <Card>
      <CardHeader
        title="Types de documents"
        subheader="Gérez les catégories de documents pour votre copropriété. Les types par défaut peuvent être renommés, pas supprimés."
        action={props.isAdmin ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={hook.handleOpenAdd}
          >
            Ajouter
          </Button>
        ) : undefined}
      />
      <Divider />

      {hook.isError ? (
        <EmptyState
          title="Types de documents indisponibles"
          subtitle="Le chargement a échoué. Rechargez la page pour réessayer."
        />
      ) : hook.isLoading ? (
        <List aria-label="Types de documents" aria-busy disablePadding>
          {SKELETON_ROWS.map((row) => (
            <ListItem key={row} divider aria-hidden="true">
              <ListItemText disableTypography primary={<Skeleton variant="text" width="35%" />} />
            </ListItem>
          ))}
        </List>
      ) : hook.documentTypes.length === 0 ? (
        <EmptyState
          title="Aucun type de document"
          subtitle="Créez des types (Assurance, Diagnostic, Contrat…) pour classer les documents de la copropriété."
        />
      ) : (
        <List aria-label="Types de documents" disablePadding>
          {hook.documentTypes.map((type, index) => (
            <ListItem
              key={type.id}
              divider={index < hook.documentTypes.length - 1}
              secondaryAction={props.isAdmin ? (
                <DocumentTypeMenuAction
                  type={type}
                  onEdit={hook.handleOpenEdit}
                  onDelete={hook.handleOpenDelete}
                />
              ) : undefined}
            >
              <ListItemText
                disableTypography
                primary={(
                  <Stack direction="row" spacing={1} alignItems="center" wrap="wrap">
                    <Typography variant="body1">{type.name}</Typography>
                    {type.is_system ? <Tag color="default" label="Par défaut" /> : null}
                  </Stack>
                )}
              />
            </ListItem>
          ))}
        </List>
      )}

      {hook.isAddModalOpen || hook.typeToEdit ? (
        <CreateEditDocumentTypeModal
          spaceId={props.spaceId}
          typeToEdit={hook.typeToEdit}
          onClose={hook.handleCloseModal}
        />
      ) : null}

      {hook.typeToDelete ? (
        <ConfirmDeleteDialog
          title="Supprimer un type de document"
          onConfirm={hook.handleConfirmDelete}
          onClose={hook.handleCloseDelete}
        >
          Êtes-vous sûr de vouloir supprimer le type de document «{NBSP}{hook.typeToDelete.name}{NBSP}»{NBSP}?
          Les documents actuellement associés à ce type seront automatiquement réassignés au type par défaut «{NBSP}Autre{NBSP}».
        </ConfirmDeleteDialog>
      ) : null}
    </Card>
  );
}
